import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t, type IntlService } from 'ember-intl';
import { PhChecks, PhSun } from 'ember-phosphor-icons';
import EmptyState from 'tio-ui/components/empty-state';
import Heading from 'tio-ui/components/heading';
import PageHero from 'tio-ui/components/page-hero';
import { Section, VStack } from 'tio-ui/components/layout';
import WidgetFrame from 'tio-employee/components/partner-dashboard/widget-frame';
import { greetingKey } from 'tio-employee/utils/greeting';
import type { DashboardSection } from 'tio-employee/utils/partner-dashboard/registry';
import type { PartnerDashboardModel } from 'tio-employee/routes/authenticated/admin/index';
import type SessionContextService from 'tio-employee/services/session-context';

interface PartnerDashboardPageSignature {
  Args: {
    /** The route's persona context plus each visible widget with its in-flight data. */
    model: PartnerDashboardModel;
  };
}

/**
 * Role-based partner dashboard: Summary (hero), Needs Your Attention,
 * Pertinent info, and Exploration, filled with the widgets the viewer's
 * personas drive. Needs Your Attention always renders, with an empty state
 * when nothing is waiting.
 */
export default class PartnerDashboardPage extends Component<PartnerDashboardPageSignature> {
  @service declare intl: IntlService;
  @service declare sessionContext: SessionContextService;

  get greeting() {
    const base = this.intl.t(greetingKey(new Date().getHours()));
    const name = this.sessionContext.currentEmployee?.firstName;
    return name ? `${base}, ${name}` : base;
  }

  get heroWidgets() {
    return this.#widgetsIn('hero');
  }

  get attentionWidgets() {
    return this.#widgetsIn('attention');
  }

  /** Pertinent info, then Exploration, each under its own heading; a section with no widgets is omitted. */
  get lowerSections() {
    return [
      { section: 'pertinent' as const, titleKey: 'partner_dashboard.pertinent_title' },
      { section: 'explore' as const, titleKey: 'partner_dashboard.explore_title' },
    ]
      .map((group) => ({ ...group, widgets: this.#widgetsIn(group.section) }))
      .filter(({ widgets }) => widgets.length > 0);
  }

  #widgetsIn(section: DashboardSection) {
    return this.args.model.widgets.filter((widget) => widget.definition.section === section);
  }

  <template>
    <VStack class="gap-8" data-test-partner-dashboard>
      <PageHero @gradient="brand-soft" class="tio-elevation-card-raised">
        <:default>
          <div class="tio-col-extra-loose">
            <div>
              <div class="tio-row-tight tio-text-sm-semibold app-text-brand">
                <PhSun class="tio-icon-sm" />
                <span class="pii">{{this.greeting}}</span>
              </div>
              <Heading @level={{1}}>{{t "partner_dashboard.title"}}</Heading>
            </div>
            {{#if this.heroWidgets.length}}
              <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
                {{#each this.heroWidgets as |widget|}}
                  <WidgetFrame @widget={{widget}} />
                {{/each}}
              </div>
            {{/if}}
          </div>
        </:default>
      </PageHero>

      <Section data-test-partner-dashboard-attention>
        <:header>{{t "partner_dashboard.needs_attention"}}</:header>
        <:body>
          {{#if this.attentionWidgets.length}}
            <div class="tio-col">
              {{#each this.attentionWidgets as |widget|}}
                <WidgetFrame @widget={{widget}} />
              {{/each}}
            </div>
          {{else}}
            <EmptyState
              @icon={{PhChecks}}
              @title={{t "partner_dashboard.needs_attention_empty_title"}}
              @description={{t "partner_dashboard.needs_attention_empty_body"}}
              @compact={{true}}
            />
          {{/if}}
        </:body>
      </Section>

      {{#each this.lowerSections as |group|}}
        <Section data-test-partner-dashboard-section={{group.section}}>
          <:header>{{t group.titleKey}}</:header>
          <:body>
            <div class="tio-col-extra-loose">
              {{#each group.widgets as |widget|}}
                <WidgetFrame @widget={{widget}} />
              {{/each}}
            </div>
          </:body>
        </Section>
      {{/each}}
    </VStack>
  </template>
}
